import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Button, Typography, Box } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { getTranslation } from '../translations';
import { useLanguage } from '../contexts/LanguageContext';

// Date d'ouverture des inscriptions : 23 juin 2025 à 19h
const REGISTRATION_OPEN_DATE = new Date('2025-06-23T12:00:00');

const DynamicRegisterButton = ({ variant = "contained", size = "large", fullWidth = false }) => {
  const { language } = useLanguage();
  const [timeRemaining, setTimeRemaining] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });
  const [isRegistrationOpen, setIsRegistrationOpen] = useState(false);
  const [showAnimation, setShowAnimation] = useState(false);

  // Calculer le temps restant jusqu'à l'ouverture des inscriptions
  useEffect(() => {
    const calculateTimeRemaining = () => {
      const now = new Date();
      const isOpen = now >= REGISTRATION_OPEN_DATE;
      
      setIsRegistrationOpen(isOpen);
      
      if (isOpen) return;
      
      const totalSeconds = Math.floor((REGISTRATION_OPEN_DATE - now) / 1000);
      const days = Math.floor(totalSeconds / (60 * 60 * 24));
      const hours = Math.floor((totalSeconds % (60 * 60 * 24)) / (60 * 60));
      const minutes = Math.floor((totalSeconds % (60 * 60)) / 60);
      const seconds = Math.floor(totalSeconds % 60);
      
      setTimeRemaining({ days, hours, minutes, seconds });
    };

    // Calculer immédiatement
    calculateTimeRemaining();
    
    // Puis mettre à jour toutes les secondes
    const interval = setInterval(calculateTimeRemaining, 1000);
    
    return () => clearInterval(interval);
  }, []);
  
  // Effet séparé pour gérer l'animation
  useEffect(() => {
    // Vérifier une seule fois si la date d'ouverture est atteinte
    if (new Date() >= REGISTRATION_OPEN_DATE && !showAnimation) {
      setShowAnimation(true);
    }
  }, []);

  // Formater le compte à rebours
  const formattedCountdown = `${timeRemaining.days}j ${timeRemaining.hours}h ${timeRemaining.minutes}m ${timeRemaining.seconds}s`;

  return (
    <Box sx={{ textAlign: 'center' }}>
      <AnimatePresence mode="wait">
        {isRegistrationOpen ? (
          <motion.div
            key="open"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.5 }}
          >
            <Button
              component={RouterLink}
              to="/reservation-pass"
              variant={variant}
              color="primary"
              size={size}
              fullWidth={fullWidth}
              sx={{ 
                px: 3,
                py: size === 'large' ? 1.5 : 1,
                fontWeight: 600
              }}
            >
              {getTranslation('nav.register', language)}
            </Button>
          </motion.div>
        ) : (
          <motion.div
            key="closed"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.5 }}
          >
            <Button
              disabled
              variant={variant}
              color="primary"
              size={size}
              fullWidth={fullWidth}
              sx={{ 
                px: 3,
                py: size === 'large' ? 1.5 : 1,
                opacity: 0.7,
                mb: 1
              }}
            >
              {getTranslation('home.registerSoon', language)}
            </Button>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {getTranslation('home.registrationOpens', language)} <strong>23 juin à 12h</strong>
            </Typography>
            <Typography 
              variant="body2" 
              fontWeight="bold" 
              sx={{ 
                color: 'primary.main',
                animation: 'pulse 2s infinite'
              }}
            >
              {formattedCountdown}
            </Typography>
            <style jsx>{`
              @keyframes pulse {
                0% { opacity: 0.7; }
                50% { opacity: 1; }
                100% { opacity: 0.7; }
              }
            `}</style>
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  );
};

export default DynamicRegisterButton;
